import { Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateLogMutation } from "src/api/services/logsApi";
import { useUpdateMeMutation } from "src/api/services/patientApi";
import {
  useCreateVisitMutation,
  useGetVisitsListQuery,
} from "src/api/services/visitApi";
import Loading from "src/components/Loading/Loading";
import { Paths } from "src/constants/paths";
import { useSession } from "src/context/SessionContext";
import { isValidLanguage } from "src/utils/isValidLanguage";
import { useCreateMeMutation } from "src/api/services/patientApi";

const SocialLoginTransition = () => {
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [createVisit] = useCreateVisitMutation();
  const [updateMe] = useUpdateMeMutation();
  const product = localStorage.getItem("product");
  const isSignUp = localStorage.getItem("isSignUp");
  const variant = localStorage.getItem("variant");
  const creationRef = useRef(false);
  const { i18n } = useTranslation();
  const [createLog] = useCreateLogMutation();
  const [createMe] = useCreateMeMutation();
  const [searchParams] = useSearchParams();

  const referralCode = useMemo(
    () => searchParams.get("referral_code"),
    [searchParams]
  );

  const { data: visits = [], isLoading: loadingVisits } = useGetVisitsListQuery(
    undefined,
    { skip: !session }
  );

  useEffect(() => {
    if (loading || !session || loadingVisits) return;

    if (creationRef.current) return;

    if (isSignUp !== "true" && visits.length > 0) {
      createLog({
        level: "info",
        data: {
          message: `${JSON.stringify({
            isSignUp,
            visitsLength: visits.length,
            product,
          })}`,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      });
      creationRef.current = true;
      localStorage.removeItem("isSignUp");
      localStorage.removeItem("product");
      navigate(Paths.AUTH_QUEUE, { replace: true });
      return;
    }

    creationRef.current = true;
    const run = async () => {
      if (!product) {
        setError("Oops, something went wrong. No product selected.");
        return;
      }

      const deviceId = window.freshpaint?.cookie?.props?.$device_id;

      if (visits.length > 0) {
        createLog({
          level: "info",
          data: {
            message: `${JSON.stringify({
              isSignUp,
              visitsLength: visits.length,
              product,
              deviceId,
            })}`,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          },
        });
        localStorage.removeItem("isSignUp");
        navigate(Paths.AUTH_QUEUE, { replace: true });
        return;
      }

      await createMe({
        profile_id: session.user.id,
        referred_by_code: referralCode ?? undefined,
        profile: {
          language: isValidLanguage(i18n.language) ? i18n.language : undefined,
        },
        metadata: {
          fp_device_id: window.freshpaint?.cookie?.props?.$device_id,
        },
      }).unwrap();

      await createVisit({
        product,
        metadata: variant != null ? { product_variant: variant } : undefined,
      }).unwrap();

      localStorage.removeItem("product");
      localStorage.removeItem("isSignUp");
      localStorage.removeItem("variant");
      navigate(Paths.AUTH_QUEUE, { replace: true });
    };

    run();
  }, [
    loading,
    session,
    isSignUp,
    product,
    createVisit,
    navigate,
    loadingVisits,
    visits.length,
    updateMe,
    i18n.language,
    createLog,
    createMe,
    variant,
    referralCode,
  ]);

  if (error) {
    return (
      <Typography sx={{ color: "red" }} textAlign="center">
        {error.toString()}
      </Typography>
    );
  }

  return <Loading />;
};

export default SocialLoginTransition;
